import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import {
  extractReaderCharacters,
  extractReaderGrammarConceptIds,
  extractReaderWordIds
} from '@/lib/familiarity-core'
import {
  getContentFamiliaritySummary,
  getSavedGrammarExampleCount,
  getWordFamiliarityStatuses
} from '@/lib/familiarity'
import type { ReaderContent } from '@/types/reader'

function stableIdsKey(ids: Array<number | string>) {
  return ids.join(',')
}

export function useContentFamiliarity(content: ReaderContent) {
  const { user } = useAuth()
  const wordIds = useMemo(() => extractReaderWordIds(content), [content])
  const characterTexts = useMemo(
    () => extractReaderCharacters(content),
    [content]
  )
  const grammarIds = useMemo(
    () => extractReaderGrammarConceptIds(content),
    [content]
  )
  const contentKey =
    content.sourceType === 'story' && content.metadata?.slug
      ? content.metadata.slug
      : content.id

  return useQuery({
    queryKey: [
      'content-familiarity',
      user?.id,
      content.sourceType,
      contentKey,
      stableIdsKey(wordIds),
      stableIdsKey(characterTexts),
      stableIdsKey(grammarIds)
    ],
    queryFn: () => getContentFamiliaritySummary(content),
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5
  })
}

export function useWordFamiliarity(entryId: number | null | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['word-familiarity', user?.id, entryId],
    queryFn: async () => {
      if (!entryId) return 'new'
      return (await getWordFamiliarityStatuses([entryId])).get(entryId) ?? 'new'
    },
    enabled: Boolean(user?.id && entryId),
    staleTime: 1000 * 60 * 5
  })
}

export function useSavedGrammarExampleCount(conceptId: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['grammar-saved-example-count', user?.id, conceptId],
    queryFn: () => getSavedGrammarExampleCount(conceptId),
    enabled: Boolean(user?.id && conceptId),
    staleTime: 1000 * 60 * 5
  })
}
